import {
    PrologStationPoolDto,
    StationContextBootstrapResponse,
    StationContextDto,
} from '@mark43/cad-api';

/**
 * One all-stations staffing-grid row. Only `total` is editable; the rest are the server-computed
 * read-only pool figures (total minus the crews committed to active events) that ride along for display and the over-commit warning.
 */
export type StaffingPoolRow = {
    stationId: number;
    label: string;
    total: string;
    /** The total as loaded, so Save sends only the stations the dispatcher changed. */
    initialTotal: string;
    /**
     * Whether the station has a stored total. A station without one runs unconstrained in Prolog, which is
     * not the same as a total of 0 (no crew at all), so it loads blank and is only saved once a total is typed.
     */
    hasTotal: boolean;
    computedAvailable: number | null;
    committedCount: number;
    committedUnits: PrologStationPoolDto['committedUnits'];
};

/**
 * Whether a station has a stored total. The bootstrap reports a missing total as 0, so it can't tell the two
 * apart on its own; the computed pool can, because Prolog leaves untotalled stations out of it. A station
 * with no stored row, or with a 0 and no pool entry, is treated as having no total. If the pool failed to
 * load, a real 0 loads blank too, which is safe: a blank, untouched station is never saved.
 */
const stationHasTotal = (
    context: StationContextDto | undefined,
    pool: PrologStationPoolDto | undefined
): boolean => context != null && (context.totalPersonnelCount > 0 || pool != null);

/** Builds the grid rows from the bootstrap response, sorted by station label. */
export const buildRows = (data: StationContextBootstrapResponse | undefined): StaffingPoolRow[] => {
    const contexts = data?.stationContextByStationId ?? {};
    const pools = data?.prologStationPoolByStationId ?? {};
    return (data?.stations ?? [])
        .slice()
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((station) => {
            const context = contexts[station.value];
            const pool = pools[station.value];
            const hasTotal = stationHasTotal(context, pool);
            const total = hasTotal ? String(context?.totalPersonnelCount ?? 0) : '';
            return {
                stationId: station.value,
                label: station.label,
                total,
                initialTotal: total,
                hasTotal,
                computedAvailable: pool?.computedAvailablePersonnelCount ?? null,
                committedCount: pool?.committedPersonnelCount ?? 0,
                committedUnits: pool?.committedUnits ?? [],
            };
        });
};

/** Over-commit: committed crews exceed the total the dispatcher has entered (a blank total counts as 0). */
export const isOverCommitted = (row: StaffingPoolRow): boolean =>
    row.committedCount > (parseInt(row.total, 10) || 0);

/**
 * The Available column cell: the computed available count, or an em dash when the station has no
 * computed pool (never seeded). The committed units are shown separately in the modal's Committed column.
 */
export const availableLabel = (row: StaffingPoolRow): string =>
    row.computedAvailable == null ? '—' : row.computedAvailable.toString();

/** Whether the dispatcher changed this station's total. Blank and unchanged totals are not changes. */
const isTotalChanged = (row: StaffingPoolRow): boolean =>
    row.total.trim() !== '' && parseInt(row.total, 10) !== parseInt(row.initialTotal, 10);

/**
 * The save payload: only the stations whose total the dispatcher changed. Untouched stations are not sent,
 * so a save never rewrites a station nobody edited, and a station without a total stays without one.
 *
 * Only the total is sent. The backend shifts each station's stored available count by the change in its
 * total, and rejects the save if a station's stored available count already exceeds its total. Legacy,
 * non-Prolog cross-staffing reads that stored count. Sending only edited stations means such a station
 * blocks a save only when the dispatcher edits it.
 */
export const toStationContexts = (rows: StaffingPoolRow[]): StationContextDto[] =>
    rows.filter(isTotalChanged).map((row) => ({
        stationId: row.stationId,
        totalPersonnelCount: parseInt(row.total, 10),
    }));

/** Case-insensitive match of the filter box against the station label; a blank query matches every row. */
export const matchesFilter = (row: StaffingPoolRow, query: string): boolean =>
    row.label.toLowerCase().includes(query.trim().toLowerCase());
